import React, { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";

/**
 * Weekly shopping list modal.
 * Fetches /api/plan/shopping-list and renders category-grouped items with
 * practical units (eggs/bananas/slices) plus an inline PDF download button.
 */
const ShoppingListModal = ({ profile, onClose }) => {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.post(`/plan/shopping-list?lang=${lang}`, { profile })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [profile, lang]);

  const handlePdf = async () => {
    setDownloading(true);
    try {
      const res = await api.post("/plan/shopping-list/pdf",
        { profile, language: lang },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lista_cumparaturi.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      data-testid="shopping-list-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "2rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#141414", border: "1px solid #27272A", maxWidth: "720px",
          width: "100%", maxHeight: "90vh", overflow: "auto", padding: "1.75rem",
        }}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#FF3B30]">
              {lang === "ro" ? "LISTA SĂPTĂMÂNALĂ" : "WEEKLY LIST"}
            </div>
            <h2 className="font-heading text-2xl mt-1">
              {lang === "ro" ? "Lista de cumpărături" : "Shopping list"}
            </h2>
            {data && !data.error && (
              <div className="text-sm text-white/60 mt-1">
                {data.total_items} {lang === "ro" ? "produse" : "items"} ·{" "}
                {(data.total_grams / 1000).toFixed(1)} kg
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-shopping-list-btn"
            style={{ background: "transparent", border: "1px solid #2A2A2A", color: "#fff",
                     width: "32px", height: "32px", cursor: "pointer", fontSize: "16px" }}
          >
            ×
          </button>
        </div>

        {!data && (
          <div className="text-white/60 text-center py-8">
            {lang === "ro" ? "Se calculează lista..." : "Building list..."}
          </div>
        )}

        {data?.error && (
          <div className="text-[#FF3B30] text-center py-8">
            {lang === "ro" ? "Eroare la încărcare." : "Failed to load."}
          </div>
        )}

        {data?.categories?.map((cat) => (
          <div key={cat.key} className="mb-5" data-testid={`shopping-cat-${cat.key}`}>
            <div className="font-heading text-base mb-2 pb-1" style={{ borderBottom: "1px solid #27272A" }}>
              {cat.label}
            </div>
            <div className="space-y-1">
              {cat.items.map((it) => (
                <div
                  key={it.food_key}
                  className="flex justify-between items-baseline text-sm py-1"
                  style={{ borderBottom: "1px dotted #222" }}
                  data-testid={`shopping-item-${it.food_key}`}
                >
                  <span className="text-white/85">{it.name}</span>
                  <span className="font-mono text-xs text-[#F59E0B]">{it.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {data && !data.error && (
          <div className="mt-6 pt-4 flex gap-3 justify-end" style={{ borderTop: "1px solid #27272A" }}>
            <button
              type="button"
              onClick={onClose}
              data-testid="shopping-cancel-btn"
              style={{ background: "transparent", border: "1px solid #2A2A2A", color: "#fff",
                       padding: "8px 16px", cursor: "pointer", fontSize: "11px", letterSpacing: "0.1em" }}
              className="font-mono"
            >
              {lang === "ro" ? "ÎNCHIDE" : "CLOSE"}
            </button>
            <button
              type="button"
              onClick={handlePdf}
              disabled={downloading}
              data-testid="shopping-pdf-btn"
              style={{ background: "#FF3B30", border: "none", color: "#fff",
                       padding: "8px 16px", cursor: downloading ? "wait" : "pointer",
                       fontSize: "11px", letterSpacing: "0.1em", opacity: downloading ? 0.6 : 1 }}
              className="font-mono"
            >
              {downloading
                ? (lang === "ro" ? "SE DESCARCĂ..." : "DOWNLOADING...")
                : (lang === "ro" ? "DESCARCĂ PDF" : "DOWNLOAD PDF")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingListModal;
